import { FC, useState } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { Check } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { useFormik } from 'formik'
import { useIntl } from 'react-intl'
import dayjs from 'dayjs'

import BackNavigationHeader, { BackNavigationContent } from 'components/BackNavigationHeader'
import { useUsersProfile } from 'services/profile/profile.hooks'
import { isMaskedValue, maskSensitiveValue } from 'utils'
import { useLanguage } from 'services/i18n/i18n.hooks'
import countries, { INationality } from 'consts/countries'
import theme from 'styles/theme'

const formatDateOfBirth = (value?: string | null) => {
  if (!value) return ''
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format('DD.MM.YYYY') : value
}

const formatPlaceOfBirth = (value?: string | number | null) => {
  if (value === null || value === undefined || value === '') return ''
  return String(value)
}

const fieldSx = (locked: boolean, labelBg: string) => ({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    backgroundColor: locked ? '#f9fafb' : '#fff',
    fontSize: '14px',
    color: locked ? theme.r4r.grey : theme.r4r.ink,
    '& fieldset': { borderColor: theme.r4r.greyLight },
    '&:hover fieldset': { borderColor: theme.r4r.greyLight },
    '&.Mui-focused fieldset': { borderColor: theme.r4r.navy, borderWidth: '1px' }
  },
  '& .MuiOutlinedInput-input': {
    py: '17px',
    px: '17px',
    height: '17px',
    boxSizing: 'content-box' as const,
    '&::placeholder': {
      color: '#C5C7CC',
      opacity: 1
    }
  },
  '& .MuiInputLabel-root': {
    fontSize: '11px',
    fontWeight: 600,
    color: `${theme.r4r.grey} !important`,
    backgroundColor: labelBg,
    px: '6px',
    transform: 'translate(14px, -8px) scale(1)'
  },
  '& .MuiInputLabel-root.MuiInputLabel-shrink': {
    transform: 'translate(14px, -8px) scale(1)'
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: `${theme.r4r.grey} !important`
  }
})

const UserPersonalDetails: FC = () => {
  const { formatMessage } = useIntl()
  const muiTheme = useTheme()
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up('md'))
  const { data, usersProfilePartialUpdate } = useUsersProfile()
  const { language } = useLanguage()
  const labelBg = isDesktop ? theme.r4r.greyBg : '#fff'
  const noData = formatMessage({ id: 'userPersonalDetails.noData', defaultMessage: 'Brak danych' })
  // Sensitive fields show a masked value until focused; the real value is only
  // swapped in for editing, so formik state always holds the unmasked data.
  const [revealedField, setRevealedField] = useState<string | null>(null)

  const maskedFieldProps = (name: 'idCard' | 'passportId', value: string) => ({
    value: revealedField === name ? value : maskSensitiveValue(value),
    onFocus: () => setRevealedField(name),
    onBlur: () => setRevealedField(null)
  })

  const form = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: data?.firstName || '',
      lastName: data?.lastName || '',
      pesel: data?.pesel || '',
      idCard: data?.idCard || '',
      passportId: data?.passportId || '',
      nationality: data?.nationality || '',
      dateOfBirth: formatDateOfBirth(data?.dateOfBirth),
      placeOfBirth: formatPlaceOfBirth(data?.placeOfBirth)
    },
    onSubmit: async (values) => {
      // Never send masked values back — once the backend starts returning
      // masked idCard/passportId, echoing them in the PATCH would overwrite
      // the real data with asterisks.
      await usersProfilePartialUpdate.mutateAsync({
        firstName: values.firstName,
        lastName: values.lastName,
        nationality: values.nationality,
        placeOfBirth: values.placeOfBirth,
        ...(isMaskedValue(values.idCard) ? {} : { idCard: values.idCard }),
        ...(isMaskedValue(values.passportId) ? {} : { passportId: values.passportId })
      })
    }
  })

  const nationalityValue =
    countries.find((country) => country.alpha2.toUpperCase() === form.values.nationality) || null

  const content = (
    <>
      {isDesktop && (
        <Typography
          sx={{ fontSize: '18px', fontWeight: 800, color: theme.r4r.navy, mb: '28px' }}>
          {formatMessage({ id: 'userPersonalDetails.label', defaultMessage: 'Dane osobowe' })}
        </Typography>
      )}

      <Box
        component='form'
        onSubmit={form.handleSubmit}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          columnGap: '28px',
          rowGap: '20px'
        }}>
        <TextField
          fullWidth
          name='firstName'
          label={formatMessage({ id: 'userPersonalDetails.form.firstName', defaultMessage: 'Imię' })}
          value={form.values.firstName}
          onChange={form.handleChange}
          InputLabelProps={{ shrink: true }}
          sx={fieldSx(false, labelBg)}
        />
        <TextField
          name='lastName'
          label={formatMessage({
            id: 'userPersonalDetails.form.lastName',
            defaultMessage: 'Nazwisko'
          })}
          value={form.values.lastName}
          onChange={form.handleChange}
          InputLabelProps={{ shrink: true }}
          sx={fieldSx(false, labelBg)}
        />
        <TextField
          name='pesel'
          label={formatMessage({ id: 'userPersonalDetails.form.pesel', defaultMessage: 'PESEL' })}
          value={maskSensitiveValue(form.values.pesel)}
          InputLabelProps={{ shrink: true }}
          InputProps={{ readOnly: true }}
          sx={fieldSx(true, labelBg)}
        />
        <TextField
          name='idCard'
          label={formatMessage({
            id: 'userPersonalDetails.form.idCard',
            defaultMessage: 'Numer dowodu'
          })}
          {...maskedFieldProps('idCard', form.values.idCard)}
          onChange={form.handleChange}
          InputLabelProps={{ shrink: true }}
          sx={fieldSx(false, labelBg)}
        />
        <TextField
          name='passportId'
          label={formatMessage({
            id: 'userPersonalDetails.form.passportId',
            defaultMessage: 'Numer paszportu'
          })}
          {...maskedFieldProps('passportId', form.values.passportId)}
          onChange={form.handleChange}
          placeholder={noData}
          InputLabelProps={{ shrink: true }}
          sx={fieldSx(false, labelBg)}
        />
        <Autocomplete
          fullWidth
          options={countries}
          value={nationalityValue}
          getOptionLabel={(option: INationality) =>
            String(option[language] ?? option.pl ?? option.alpha2)
          }
          isOptionEqualToValue={(option, value) => option.alpha2 === value.alpha2}
          onChange={(_, option) =>
            form.setFieldValue('nationality', option ? option.alpha2.toUpperCase() : '')
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={formatMessage({
                id: 'userPersonalDetails.form.nationality',
                defaultMessage: 'Narodowość'
              })}
              InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
              sx={fieldSx(false, labelBg)}
            />
          )}
        />
        <TextField
          name='dateOfBirth'
          label={formatMessage({
            id: 'userPersonalDetails.form.dateOfBirth',
            defaultMessage: 'Data urodzenia'
          })}
          value={form.values.dateOfBirth}
          InputLabelProps={{ shrink: true }}
          InputProps={{ readOnly: true }}
          sx={fieldSx(true, labelBg)}
        />
        <TextField
          name='placeOfBirth'
          label={formatMessage({
            id: 'userPersonalDetails.form.placeOfBirth',
            defaultMessage: 'Miejsce urodzenia'
          })}
          value={form.values.placeOfBirth}
          onChange={form.handleChange}
          InputLabelProps={{ shrink: true }}
          sx={fieldSx(false, labelBg)}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: '12px', mt: '28px', flexWrap: 'wrap' }}>
        <LoadingButton
          type='button'
          onClick={() => form.submitForm()}
          loading={form.isSubmitting}
          disabled={!form.dirty || form.isSubmitting}
          startIcon={<Check sx={{ fontSize: '16px' }} />}
          sx={{
            backgroundColor: theme.r4r.navy,
            color: '#fff',
            borderRadius: 0,
            boxShadow: 'none',
            fontSize: '13px',
            fontWeight: 700,
            lineHeight: '13px',
            px: '32px',
            py: '12px',
            ':hover': { backgroundColor: theme.r4r.ink, boxShadow: 'none' },
            '&.Mui-disabled': {
              backgroundColor: theme.r4r.greyLight,
              color: theme.r4r.grey
            }
          }}>
          {formatMessage({ id: 'userPersonalDetails.save', defaultMessage: 'Zapisz zmiany' })}
        </LoadingButton>
        <Button
          type='button'
          disabled={form.isSubmitting}
          onClick={() => form.resetForm()}
          sx={{
            border: `1px solid ${theme.r4r.greyLight}`,
            borderRadius: 0,
            color: theme.r4r.grey,
            fontSize: '13px',
            fontWeight: 600,
            lineHeight: '13px',
            px: '25px',
            py: '13px',
            ':hover': { backgroundColor: 'transparent', borderColor: theme.r4r.grey }
          }}>
          {formatMessage({ id: 'userPersonalDetails.cancel', defaultMessage: 'Anuluj' })}
        </Button>
      </Box>
    </>
  )

  if (isDesktop) {
    return content
  }

  return (
    <>
      <BackNavigationHeader
        label={formatMessage({ id: 'userPersonalDetails.label', defaultMessage: 'Dane osobowe' })}
      />
      <BackNavigationContent>{content}</BackNavigationContent>
    </>
  )
}

export default UserPersonalDetails
